import React from 'react';
import { useLanguage } from '../hooks/useLanguage';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
export function HeroSection() {
  const {
    t
  } = useLanguage();
  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section className="relative w-full pt-20 pb-24 sm:pt-32 sm:pb-40 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-b from-[#fcdce1]/20 via-[#fdfbf9] to-[#fdfbf9]" data-id="element-58">
      {/* Dot Pattern Overlay */}
      <div className="absolute inset-0 dot-pattern opacity-[0.03] pointer-events-none" data-id="element-59" />

      {/* Subtle Gradient Glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-gradient-to-b from-[#fcdce1]/20 to-transparent rounded-full blur-3xl pointer-events-none" data-id="element-60" />

      <div className="max-w-5xl mx-auto text-center relative z-10" data-id="element-61">
        {/* Decorative Line */}
        <div className="w-px h-8 sm:h-12 bg-gradient-to-b from-transparent via-[#ae6470]/30 to-[#ae6470]/30 mx-auto mb-6 sm:mb-8" style={{
        animation: 'fade-in-up 0.8s ease-out forwards'
      }} data-id="element-62" />

        {/* Badge with Border Beam Effect */}
        <div className="relative inline-flex items-center px-5 py-2 rounded-full bg-white/50 backdrop-blur-sm border border-[#ae6470]/10 text-[#ae6470] text-[11px] font-semibold uppercase tracking-[0.15em] mb-6 sm:mb-10 overflow-hidden group" style={{
        animation: 'fade-in-up 0.8s ease-out forwards'
      }} data-id="element-63">
          <span className="relative z-10" data-id="element-64">{t.hero.badge}</span>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" data-id="element-65" />
        </div>

        {/* Headline */}
        <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-[5.5rem] font-bold text-[#152c61] leading-[1.05] tracking-[-0.02em] mb-8 sm:mb-10 drop-shadow-sm" style={{
        animation: 'fade-in-up 0.8s ease-out 0.1s forwards',
        opacity: 0
      }} data-id="element-66">
          {t.hero.headline}
        </h1>

        {/* Decorative Divider */}
        <div className="flex items-center justify-center gap-4 mb-8 sm:mb-12 opacity-0" style={{
        animation: 'fade-in-up 0.8s ease-out 0.15s forwards'
      }} data-id="element-67">
          <div className="w-12 h-px bg-[#ae6470]/20" data-id="element-68" />
          <div className="w-1.5 h-1.5 rounded-full bg-[#ae6470]/40" data-id="element-69" />
          <div className="w-12 h-px bg-[#ae6470]/20" data-id="element-70" />
        </div>

        {/* Subheadline */}
        <p className="text-base sm:text-lg md:text-xl text-[#152c61]/70 max-w-2xl mx-auto leading-relaxed mb-10 sm:mb-12 font-light" style={{
        animation: 'fade-in-up 0.8s ease-out 0.2s forwards',
        opacity: 0
      }} data-id="element-71">
          {t.hero.subheadline}
        </p>

        {/* CTA */}
        <div className="flex flex-col items-center gap-8 sm:gap-12" style={{
        animation: 'fade-in-up 0.8s ease-out 0.3s forwards',
        opacity: 0
      }} data-id="element-72">
          <button onClick={scrollToContact} className="group relative inline-flex items-center px-8 py-4 bg-[#152c61] text-white text-base font-medium rounded-full overflow-hidden shadow-[0_4px_14px_rgba(21,44,97,0.2)] hover:shadow-[0_6px_20px_rgba(21,44,97,0.3)] hover:-translate-y-0.5 transition-all duration-300" data-id="element-73">
            <span className="relative z-10 flex items-center" data-id="element-74">
              {t.hero.cta}
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-75" />
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-[#152c61] to-[#1a3470]" data-id="element-76" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" data-id="element-77" />
          </button>

          {/* Feature Pills */}
          <div className="flex flex-wrap justify-center gap-4 sm:gap-8 mt-4" data-id="element-78">
            {t.hero.features?.map((feature, index) => <div key={index} className="flex items-center text-[11px] font-medium uppercase tracking-wider text-[#152c61]/60" data-id="element-79">
                <div className="w-1 h-1 rounded-full bg-[#ae6470] mr-3" data-id="element-80" />
                {feature}
              </div>)}
          </div>

          {/* Stats Row */}
          <div className="mt-12 sm:mt-20 w-full max-w-4xl border-t border-[#152c61]/5 pt-8 sm:pt-12" data-id="element-81">
            <div className="flex flex-wrap justify-center items-center gap-x-10 sm:gap-x-16 gap-y-8" data-id="element-82">
              {t.hero.stats.map((stat, index) => <div key={index} className="flex flex-col items-center group cursor-default" data-id="element-83">
                  <div className="text-3xl sm:text-4xl font-bold text-[#152c61] mb-2 tracking-tight group-hover:text-[#ae6470] transition-colors duration-300" data-id="element-84">
                    {stat.value}
                  </div>
                  <div className="text-[10px] font-semibold text-[#152c61]/40 uppercase tracking-[0.2em]" data-id="element-85">
                    {stat.label}
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}